import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import './os.css';
import { ContextTabs, StatusBadge, EmptyState, LoadingState, ErrorState, MessageComposer } from './osPrimitives';
import { ArrowLeft, Pencil, MessageSquare, ArrowRightLeft, ExternalLink, User, MoreHorizontal, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => (n === undefined || n === null || n === '') ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const humanize = (s) => (s || '').toString().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const authHeaders = () => { const t = localStorage.getItem('ace_admin_token'); return { ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
async function getJSON(path) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders() });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}
async function mutate(path, method = 'POST', body) {
  const r = await fetch(`${API}${path}`, { method, credentials: 'include', headers: { ...authHeaders(), ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || `${method} ${r.status}`);
  return j;
}

const TABS = [
  { id: 'overview', label: 'Overview' }, { id: 'client', label: 'Client' }, { id: 'service', label: 'Service' },
  { id: 'pricing', label: 'Pricing' }, { id: 'communication', label: 'Communication' }, { id: 'history', label: 'History' },
];
const STATUS_TONE = { sent: 'waiting', accepted: 'ready', confirmed: 'ready', converted: 'ready', deleted: 'risk' };

const Shell = ({ children, onBack }) => (
  <div className="os-root min-h-screen">
    <header className="sticky top-0 z-40 os-surface border-b" style={{ borderColor: 'var(--os-border)' }}>
      <div className="h-14 px-4 flex items-center gap-3 max-w-5xl mx-auto w-full">
        <button onClick={onBack} className="p-2 rounded-lg" style={{ color: 'var(--os-muted)' }} data-testid="qr-back"><ArrowLeft className="h-5 w-5" /></button>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>ACE OS · Quote Record</p>
      </div>
    </header>
    <main className="max-w-5xl mx-auto w-full px-4 py-5 os-animate">{children}</main>
  </div>
);

const Card = ({ title, children, testid, action }) => (
  <div className="os-card p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'var(--os-muted)' }}>{title}</h3>
      {action}
    </div>
    {children}
  </div>
);
const Row = ({ label, value, strong }) => (
  <div className="flex items-center justify-between py-1"><span className="text-xs" style={{ color: 'var(--os-muted)' }}>{label}</span>
    <span className="text-sm text-right" style={{ color: strong ? 'var(--os-red)' : 'var(--os-text)', fontWeight: strong ? 800 : 500 }}>{value ?? '—'}</span></div>
);

export default function QuoteRecord() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [rec, setRec] = useState({ loading: true });
  const [composer, setComposer] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [converting, setConverting] = useState(false);

  const load = useCallback(() => {
    setRec({ loading: true });
    getJSON('/api/command-center/quote-log?include_deleted=true')
      .then((d) => {
        const q = (d.quotes || []).find((x) => x.quote_id === quoteId);
        if (!q) { setRec({ loading: false, error: true }); return; }
        setRec({ loading: false, q });
      })
      .catch(() => setRec({ loading: false, error: true }));
  }, [quoteId]);
  useEffect(() => { load(); }, [load]);

  if (rec.loading) return <Shell onBack={() => navigate('/os/services?tab=quotes')}><LoadingState rows={5} /></Shell>;
  if (rec.error) return <Shell onBack={() => navigate('/os/services?tab=quotes')}><ErrorState title="Quote not found" body={`No canonical quote record for “${quoteId}”.`} onRetry={load} /></Shell>;

  const q = rec.q;
  const status = (q.status || 'sent').toLowerCase();
  const converted = !!q.booking_id || ['confirmed', 'converted'].includes(status);
  const amount = q.quoted_amount ?? q.grand_total ?? q.total;
  const openClient = () => navigate(`/os/clients/${encodeURIComponent(q.client_name || '')}`);
  const openService = () => navigate(`/os/services/${q.booking_id}`);
  const editQuote = () => navigate(`/os/services/quotes/${quoteId}/edit`);

  const convert = async () => {
    if (converting) return;
    setConverting(true);
    try {
      const r = await mutate(`/api/command-center/quote-log/${quoteId}/convert`, 'POST');
      if (r.booking_id) { toast.success(r.already_converted ? 'Already converted' : 'Quote converted to booking'); navigate(`/os/services/${r.booking_id}`); }
      else toast.error('Convert failed');
    } catch (e) { toast.error(e.message || 'Convert failed'); }
    finally { setConverting(false); }
  };
  const sendSMS = async (text) => {
    const to = q.client_phone || q.phone;
    const r = await mutate('/api/command-center/quo/send-sms', 'POST', { to, message: text });
    r.success ? toast.success('SMS sent') : toast.error('SMS failed');
    load();
  };
  const openCompose = () => { setComposerText(''); setComposer(true); };
  const textPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const r = await mutate(`/api/command-center/quote-log/${quoteId}/pdf`, 'POST');
      if (!r.token) { toast.error('Could not generate PDF'); return; }
      const link = `${window.location.origin}/quote/${r.token}`;
      const first = (q.client_name || '').split(' ')[0] || 'there';
      setComposerText(`Hi ${first}, here's your ${q.service_type || 'service'} quote from Your Auto Concierge (${money(amount)}). View, download the PDF, and accept here: ${link} — or reply to confirm.`);
      setComposer(true);
      load();
    } catch (e) { toast.error(e.message || 'PDF failed'); }
    finally { setPdfBusy(false); }
  };

  const smsHistory = q.sms_history || q.messages || [];

  return (
    <Shell onBack={() => navigate('/os/services?tab=quotes')}>
      {/* Header */}
      <div className="os-elev rounded-2xl p-5 mb-4" data-testid="quote-record-header">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>Quote · {quoteId}</p>
            <h1 className="os-display text-3xl break-words" data-testid="qr-client">{q.client_name || 'Client'}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <StatusBadge status={STATUS_TONE[status] || 'unknown'} label={humanize(status)} testid="qr-status" />
              {q.service_type && <span className="os-chip" style={{ color: 'var(--os-text)', border: '1px solid var(--os-border)' }}>{humanize(q.service_type)}</span>}
              <span className="os-chip" style={{ color: 'var(--os-red)', border: '1px solid var(--os-border)' }}>{money(amount)}</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs" style={{ color: 'var(--os-muted)' }}>
              {q.inquiry_id && <span data-testid="qr-inq-link">Lead · {q.inquiry_id}</span>}
              {q.booking_id && <button className="underline" onClick={openService} data-testid="qr-bk-link">Booking · {q.booking_id}</button>}
              <span>{(q.created_at || '').slice(0, 10)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end flex-wrap">
            {converted ? (
              <button className="os-btn-primary" onClick={openService} data-testid="qr-open-service"><ExternalLink className="h-4 w-4" /> Open Service</button>
            ) : (
              <>
                <button className="os-btn-ghost" onClick={editQuote} data-testid="qr-edit"><Pencil className="h-4 w-4" /> Edit</button>
                <button className="os-btn-ghost" onClick={textPdf} disabled={pdfBusy || !(q.client_phone || q.phone)} data-testid="qr-text-pdf"><FileText className="h-4 w-4" /> {pdfBusy ? 'Preparing…' : 'Text PDF'}</button>
                <button className="os-btn-ghost" onClick={openCompose} disabled={!(q.client_phone || q.phone)} data-testid="qr-send-sms"><MessageSquare className="h-4 w-4" /> Send SMS</button>
                <button className="os-btn-primary" onClick={convert} disabled={converting} data-testid="qr-convert"><ArrowRightLeft className="h-4 w-4" /> {converting ? 'Converting…' : 'Convert to Booking'}</button>
              </>
            )}
          </div>
        </div>
        {converted && <div className="mt-3 os-card p-3" style={{ borderColor: 'var(--os-border)' }} data-testid="qr-converted-banner">
          <p className="text-sm" style={{ color: 'var(--os-warning)' }}>This quote has been converted to a booking and is locked. Open the Service Record to make changes.</p>
        </div>}
      </div>

      <ContextTabs tabs={TABS} active={tab} onChange={setTab} testid="qr-tabs" />
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {tab === 'overview' && <>
          <Card title="Summary" testid="qr-overview">
            <Row label="Quote" value={quoteId} />
            <Row label="Status" value={humanize(status)} />
            <Row label="Amount" value={money(amount)} strong />
            <Row label="Created" value={(q.created_at || '').slice(0, 10)} />
            <Row label="Follow-up" value={q.follow_up_date || '—'} />
          </Card>
          <Card title="Pipeline" testid="qr-pipeline">
            <Row label="Lead (INQ)" value={q.inquiry_id || '—'} />
            <Row label="Quote (QT)" value={quoteId} />
            <Row label="Booking (BK)" value={q.booking_id || '—'} />
          </Card>
        </>}
        {tab === 'client' && <Card title="Client" testid="qr-client-tab">
          <Row label="Name" value={q.client_name} />
          <Row label="Phone" value={q.client_phone || q.phone} />
          <Row label="Email" value={q.client_email || q.email} />
          <div className="mt-3"><button className="os-btn-ghost" onClick={openClient} data-testid="qr-open-client"><User className="h-4 w-4" /> Open Client 360</button></div>
        </Card>}
        {tab === 'service' && <Card title="Service" testid="qr-service-tab">
          <Row label="Service type" value={humanize(q.service_type)} />
          <Row label="Event date" value={(q.event_date || '').slice(0, 10)} />
          <Row label="Pickup" value={q.pickup_location} />
          <Row label="Drop-off" value={q.dropoff_location} />
          <Row label="Passengers" value={q.passengers} />
          <Row label="Hours" value={q.hours} />
          <Row label="Vehicle / class" value={q.vehicle_name || q.vehicle} />
          <Row label="Notes" value={q.notes} />
        </Card>}
        {tab === 'pricing' && <Card title="Pricing (authoritative)" testid="qr-pricing-tab">
          <Row label="Base" value={money(q.base_total)} />
          <Row label="Gratuity" value={money(q.gratuity)} />
          <Row label="Sales tax" value={money(q.sales_tax)} />
          <Row label="Quoted amount" value={money(amount)} strong />
          {q.pricing_type && <Row label="Tier" value={humanize(q.pricing_type)} />}
          <p className="text-[11px] mt-2" style={{ color: 'var(--os-muted)' }}>Figures are set by the canonical pricing engine. Edit the quote to recalculate.</p>
        </Card>}
        {tab === 'communication' && <div className="sm:col-span-2">
          <Card title="Send" testid="qr-comm-tab" action={<div className="flex gap-2">
            <button className="os-btn-ghost" onClick={textPdf} disabled={converted || pdfBusy || !(q.client_phone || q.phone)} data-testid="qr-comm-text-pdf"><FileText className="h-4 w-4" /> {pdfBusy ? 'Preparing…' : 'Text PDF'}</button>
            <button className="os-btn-ghost" onClick={openCompose} disabled={converted || !(q.client_phone || q.phone)} data-testid="qr-comm-send"><MessageSquare className="h-4 w-4" /> Compose</button>
          </div>}>
            <p className="text-xs" style={{ color: 'var(--os-muted)' }}>SMS via the canonical messaging service. "Text PDF" generates a branded quote PDF and texts a link to a client accept page (view · download PDF · accept). Email delivery requires a verified sender configuration.</p>
            {q.pdf_token && <div className="mt-3 os-card p-3" data-testid="qr-pdf-link">
              <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--os-muted)' }}>Client accept page</p>
              <a href={`${window.location.origin}/quote/${q.pdf_token}`} target="_blank" rel="noreferrer" className="text-sm underline break-all" style={{ color: 'var(--os-red)' }}>{`${window.location.origin}/quote/${q.pdf_token}`}</a>
              <p className="text-[11px] mt-2" style={{ color: 'var(--os-muted)' }}>PDF: <a href={`${API}/api/public/quotes/${q.pdf_token}`} target="_blank" rel="noreferrer" className="underline" style={{ color: 'var(--os-muted)' }}>direct link</a></p>
            </div>}
          </Card>
          <div className="mt-4"><Card title="History" testid="qr-comm-history">
            {smsHistory.length ? smsHistory.map((m, i) => (
              <div key={i} className="py-2 border-b last:border-0" style={{ borderColor: 'var(--os-border)' }}>
                <p className="text-sm" style={{ color: 'var(--os-text)' }}>{m.text || m.message}</p>
                <p className="text-[11px]" style={{ color: 'var(--os-muted)' }}>{(m.at || m.sent_at || '').slice(0, 16).replace('T', ' ')}</p>
              </div>
            )) : <EmptyState title="No messages yet" body="Send an SMS to start the thread." />}
          </Card></div>
        </div>}
        {tab === 'history' && <div className="sm:col-span-2"><Card title="Status history" testid="qr-history-tab">
          <Row label="Created" value={(q.created_at || '').slice(0, 16).replace('T', ' ')} />
          {q.updated_at && <Row label="Updated" value={(q.updated_at || '').slice(0, 16).replace('T', ' ')} />}
          {q.converted_at && <Row label="Converted" value={(q.converted_at || '').slice(0, 16).replace('T', ' ')} />}
          <Row label="Current status" value={humanize(status)} />
        </Card></div>}
      </div>

      <MessageComposer open={composer} onClose={() => setComposer(false)} to={q.client_phone || q.phone} emailVerified={false} onSendSMS={sendSMS} initialText={composerText} testid="qr-message-composer" />
    </Shell>
  );
}
